import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Grid, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, DialogTitle, DialogContent, IconButton, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, CircularProgress, Card, CardContent, Divider } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { CloudUpload as CloudUploadIcon, Image as ImageIcon, Close as CloseIcon, Receipt as ReceiptIcon, Description as DescriptionIcon } from "@mui/icons-material";
import { getData, postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

function EmployeeReimbursements() {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const firstName = localStorage.getItem("userFirstName") || "";
    const lastName = localStorage.getItem("userLastName") || "";
    const employeeName = `${firstName} ${lastName}`.trim() || "Employee";
    const fileInputRef = useRef(null);
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [claimType, setClaimType] = useState("");
    const [payrollCycle, setPayrollCycle] = useState("");
    const [requestedAmount, setRequestedAmount] = useState("");
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const fetchMyReimbursements = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchEmployeeReimbursements(userId));
            if (res?.statusCode === 200 && Array.isArray(res.result)) {
                const flat = [];
                res.result.forEach((group) => {
                    if (group?.reimbursements) {
                        group.reimbursements.forEach((item) => {
                            flat.push({
                                id: item.id,
                                claimType: item.claimType || "Other",
                                requestedAmount: Number(item.postamount) || 0,
                                approvedAmount: Number(item.amount) || 0,
                                remarks: item.comment || "No Comments",
                                receiptUrl: item.receiptUrl,
                                status: item.status,
                                payrollCycle: item.payrollCycle || group.payrollCycle || "N/A"
                            });
                        });
                    }
                });
                setClaims(flat);
            }
        } catch (err) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId]); // Only changes if ID changes

    useEffect(() => {
        fetchMyReimbursements();
    }, [fetchMyReimbursements]);

    // ================= FILE =================
    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;

        // Validation
        if (f.type !== "application/pdf") {
            setSnackbar({ open: true, message: "Only PDF is allowed", severity: "error" });
            setFile(null);
            setFilePreview(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (f.size > 20 * 1024 * 1024) {
            setSnackbar({ open: true, message: "File size exceeds 20MB", severity: "error" });
            setFile(null);
            setFilePreview(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setFile(f);
        setFilePreview(URL.createObjectURL(f));
    };

    const removeFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ================= SUBMIT =================
    const handleSubmit = async () => {
        if (!claimType || !requestedAmount || !file) {
            setSnackbar({ open: true, message: "Please fill all fields and upload a receipt", severity: "error" });
            return;
        }

        setSubmitLoading(true);
        try {
            const fd = new FormData();
            fd.append("postamount", requestedAmount);
            fd.append("amount", requestedAmount);
            fd.append("employeePersonalId", userId);
            fd.append("employeeName", employeeName);
            fd.append("claimType", claimType);
            fd.append("payrollCycle", payrollCycle);
            fd.append("status", false); // Default status is pending (false)
            fd.append("receipt", file);

            const res = await postData(API_ENDPOINTS.createEmployeeReimbursement, fd);
            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: "Reimbursement claim submitted successfully", severity: "success" });
                setClaimType("");
                setPayrollCycle("");
                setRequestedAmount("");
                removeFile();
                fetchMyReimbursements();
            } else {
                setSnackbar({ open: true, message: res?.message || "Failed to submit claim", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: "An error occurred during submission", severity: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    // ================= UI =================
    return (
        <Box>
            <Grid container spacing={3}>
                {/* NEW CLAIM FORM */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Submit New Claim
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Claim Type</InputLabel>
                                        <Select
                                            value={claimType}
                                            onChange={(e) => setClaimType(e.target.value)}
                                            label="Claim Type"
                                        >
                                            <MenuItem value="Medical">Medical</MenuItem>
                                            <MenuItem value="Travel">Travel</MenuItem>
                                            <MenuItem value="Internet">Internet</MenuItem>
                                            <MenuItem value="Fuel">Fuel</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Payroll Cycle removed - HR will assign this during approval */}

                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Requested Amount"
                                        type="number"
                                        value={requestedAmount}
                                        onChange={(e) => setRequestedAmount(e.target.value)}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Box
                                        sx={{
                                            border: "2px dashed",
                                            borderColor: file ? "primary.main" : "grey.300",
                                            borderRadius: 2,
                                            p: 2,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            bgcolor: file ? `${'primary.light'}10` : "transparent",
                                            "&:hover": { borderColor: "primary.main", bgcolor: `${'primary.light'}05` },
                                            transition: "all 0.3s ease"
                                        }}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <input
                                            hidden
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept="application/pdf,image/*"
                                        />
                                        <CloudUploadIcon color={file ? "primary" : "action"} sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {file ? file.name : "Click to upload receipt"}
                                        </Typography>
                                        <Typography variant="caption" display="block" color="primary" sx={{ mt: 1, fontWeight: 'medium' }}>
                                            (PDF Only, Max 20MB)
                                        </Typography>
                                    </Box>
                                </Grid>

                                {filePreview && (
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{ position: "relative", width: "100%", height: 100, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
                                            <IconButton
                                                size="small"
                                                sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(0,0,0,0.5)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                                                onClick={(e) => { e.stopPropagation(); removeFile(); }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#f5f5f5">
                                                <DescriptionIcon color="action" />
                                                <Typography variant="caption" sx={{ ml: 1 }}>PDF Selected: {file.name}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}

                                <Grid size={{ xs: 12 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={submitLoading}
                                        sx={{ py: 1, fontWeight: "bold", borderRadius: 2, textTransform: "none" }}
                                    >
                                        {submitLoading ? <CircularProgress size={24} color="inherit" /> : "Submit Claim"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* CLAIMS TABLE */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box p={3}>
                                <Typography variant="h6" fontWeight="bold">My Claims History</Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ width: "100%", overflowX: "auto" }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Cycle</TableCell>
                                            <TableCell align="right">Requested</TableCell>
                                            <TableCell align="right">Approved</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell>Remarks</TableCell>
                                            <TableCell align="center">Proof</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                    <CircularProgress size={30} />
                                                </TableCell>
                                            </TableRow>
                                        ) : claims.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">No reimbursement claims found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            claims.map((row) => (
                                                <TableRow key={row.id} hover>
                                                    <TableCell sx={{ fontWeight: 500 }}>{row.claimType}</TableCell>
                                                    <TableCell>{row.payrollCycle}</TableCell>
                                                    <TableCell align="right">₹{row.requestedAmount.toLocaleString()}</TableCell>
                                                    <TableCell align="right">₹{row.approvedAmount.toLocaleString()}</TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{
                                                                display: "inline-block",
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: 10,
                                                                fontSize: "0.75rem",
                                                                fontWeight: "bold",
                                                                bgcolor: row.status ? `${'success.light'}30` : `${'warning.light'}30`,
                                                                color: row.status ? "success.dark" : "warning.dark",
                                                            }}
                                                        >
                                                            {row.status ? "Approved" : "Pending"}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "0.85rem", color: "text.secondary", maxWidth: 150 }}>
                                                        {row.remarks}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.receiptUrl && (
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => {
                                                                    setPreviewUrl(row.receiptUrl);
                                                                    setPreviewType(
                                                                        row.receiptUrl.toLowerCase().endsWith(".pdf") ? "pdf" : "img"
                                                                    );
                                                                    setOpenPreview(true);
                                                                }}
                                                            >
                                                                <ImageIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            {/* PREVIEW DIALOG */}
            <AppDialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Receipt Preview
                    <IconButton onClick={() => setOpenPreview(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {previewType === "pdf" ? (
                        <embed src={previewUrl} type="application/pdf" width="100%" height="500px" />
                    ) : (
                        <Box display="flex" justifyContent="center">
                            <img src={previewUrl} alt="receipt" style={{ maxWidth: "100%", maxHeight: "70vh" }} />
                        </Box>
                    )}
                </DialogContent>
            </AppDialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default EmployeeReimbursements;